import {
    FormValues,
    getFormValuesFromQuery,
    getForSaleFilters,
    getToRentFilters,
    getSearchPath,
    getQ
} from '@zoopla/listing-search/filters/modal';
import { Filters } from '@zoopla/redux-reducer-filters';
import { NextRouter } from 'next/router';
import { formatIdentifier } from './resultsPageNavigation';

export type FiltersTrackingObj = Omit<
    Filters,
    'features' | 'propertySubType'
> & {
    features: string[];
    propertySubType: string[];
};

type CombinedFilters = FormValues &
    ReturnType<typeof getForSaleFilters> &
    ReturnType<typeof getToRentFilters> & {
        identifier: string;
        location: string | string[];
        q: string | string[];
    };

/**
 * We have event tracking behavior that expects a filters object with the filter
 * state. This was previously pulled from redux, but filters are no longer
 * controlled by redux. So this lib creates the object using our new approach
 * of pulling "query state" from the url search query
 */

export const getIdentifier = (query: NextRouter['query']) =>
    formatIdentifier(getSearchPath(query));

const getLocation = (query: NextRouter['query']) => {
    const q = getQ(query);
    const coercedQ = Array.isArray(q) ? q[0] : q;
    return coercedQ;
};

/**
 * Record<string, string | string[]> -> Record<string, string> where the val is either the string or arr[0]
 */
function coerceString<T extends Record<string, string | string[]>>(
    obj: T
): { [K in keyof T]: string } {
    const fixed = {} as { [K in keyof T]: string };
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const val = obj[key];
            const strVal: string = Array.isArray(val) ? val[0] : val;
            fixed[key] = strVal;
        }
    }
    return fixed;
}

function combineFilters(query: NextRouter['query'], pathname: string) {
    /* there are shared keys in to-rent/for-sale filters so order the apply accordingly */
    const isToRent = pathname.includes('/to-rent/');
    const identifier = getIdentifier(query);
    const location = getLocation(query);
    const q = getQ(query);
    if (isToRent) {
        return {
            ...getForSaleFilters(query, pathname),
            ...getFormValuesFromQuery({ pathname, query }),
            ...getToRentFilters(query),
            identifier,
            location,
            q
        };
    }
    return {
        ...getToRentFilters(query),
        ...getFormValuesFromQuery({ pathname, query }),
        ...getForSaleFilters(query, pathname),
        identifier,
        location,
        q
    };
}

export const formatFiltersForTracking = (
    combinedFilters: CombinedFilters
): FiltersTrackingObj => {
    const {
        radius,
        beds_min,
        beds_max,
        property_type: propertyType,
        price_min,
        price_max,
        price_frequency: priceFrequency,
        q,
        added: propertyAdded,
        available_from: availableFrom,
        include_sold: underOfferOrSold,
        chain_free: chainFree,
        reduced_price_only: priceReduced,
        is_shared_accommodation: isSharedAccomodation,
        include_rented: includeRented,
        furnished_state: furnishings,
        bills_included: billsIncluded,
        pets_allowed: petsAllowed,
        is_shared_ownership: isSharedOwnership,
        is_auction: auction,
        is_retirement_home: retirementHome,
        new_homes: newHomes,
        buyer_incentive: helpToBuy,
        duration,
        transport_type: transportType,
        keywords,
        identifier,
        location: locationText
    } = coerceString(combinedFilters);

    const price_max_monthly = priceFrequency === 'per_month' ? price_max : '';
    const price_min_monthly = priceFrequency === 'per_month' ? price_min : '';
    const price_max_weekly = priceFrequency === 'per_week' ? price_max : '';
    const price_min_weekly = priceFrequency === 'per_week' ? price_min : '';

    return {
        radius,
        beds: {
            beds_min,
            beds_max,
            buttonTitle: ''
        },
        propertyType,
        price: { price_max, price_min, buttonTitle: '' },
        priceWeekly: {
            price_max_weekly,
            price_min_weekly,
            buttonTitle: ''
        },
        priceMonthly: {
            price_max_monthly,
            price_min_monthly,
            buttonTitle: ''
        },
        priceFrequency,
        propertySubType: combinedFilters.property_sub_type,
        propertyAdded,
        availableFrom,
        underOfferOrSold,
        chainFree,
        priceReduced,
        includeShared: isSharedAccomodation !== 'false' ? 'true' : 'false',
        includeLet: includeRented,
        furnishings,
        shortLets: 'false',
        billsIncluded,
        petsAllowed,
        features: combinedFilters.feature,
        isSharedOwnership: isSharedOwnership,
        auction,
        retirementHome,
        newHomes,
        helpToBuy,
        duration,
        transportType,
        keywords,
        location: {
            value: q,
            id: identifier
        },
        locationText,
        locationChanged: 'false'
    };
};

export function getAllFilterStateForTracking(
    query: NextRouter['query'],
    pathname: string
) {
    return formatFiltersForTracking(combineFilters(query, pathname));
}
